import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { formatDate, formatCurrency, getLicenseStatusColor, isLicenseExpired } from "@/lib/utils";
import { License } from "@shared/schema";
import { CreditCard, Calendar, Info, User, Building2 } from "lucide-react";

type ViewTeamLicensesDialogProps = {
  teamId: number | null;
  teamName?: string;
  isOpen: boolean;
  onClose: () => void;
};

export default function ViewTeamLicensesDialog({
  teamId,
  teamName = "Team",
  isOpen,
  onClose,
}: ViewTeamLicensesDialogProps) {
  // Query licenses assigned to this team
  const { data: assignedLicenses = [], isLoading } = useQuery<License[]>({
    queryKey: ['/api/teams', teamId, 'licenses'],
    queryFn: async () => {
      if (!teamId) throw new Error("Team ID is required");
      try {
        const res = await fetch(`/api/teams/${teamId}/licenses`);
        if (!res.ok) return [];
        return res.json();
      } catch (error) {
        console.error("Error fetching team licenses:", error);
        return [];
      }
    },
    enabled: isOpen && teamId !== null,
  });

  if (!isOpen) return null;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Assigned Licenses for {teamName}</DialogTitle>
          <DialogDescription>
            View all licenses assigned to this team
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : assignedLicenses.length === 0 ? (
          <div className="text-center p-6 bg-slate-50 rounded-md">
            <p className="text-muted-foreground">No licenses assigned to this team.</p>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            {assignedLicenses.map((license) => (
              <div key={license.id} className="bg-slate-50 rounded-md p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium text-base">{license.name}</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      {license.billingCycle} • Cost: {formatCurrency(license.cost)}
                    </p>
                  </div>
                  {(() => {
                    const isLifetime = license.billingCycle === 'Lifetime' || license.billingCycle === 'One-Time';
                    const effectivelyExpired = !isLifetime && license.status !== 'discontinued' && isLicenseExpired(license.renewalDate);
                    const displayStatus = effectivelyExpired ? 'expired' : (license.status || 'unknown');
                    return (
                      <Badge className={getLicenseStatusColor(displayStatus)}>
                        {displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)}
                      </Badge>
                    );
                  })()}
                </div>
                
                <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Start: {formatDate(license.startDate || null)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="h-4 w-4 text-muted-foreground" />
                    <span>Renewal: {formatDate(license.renewalDate || null)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <span>Total Seats: {license.totalSeats}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4 text-muted-foreground" />
                    <span>Assigned Seats: {license.assignedSeats}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}